'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import * as CustomPropTypes from '../../../constants/PropTypes';
import Immutable from 'immutable';
import { DROPDOWN_ITEM_DEFAULT_HEIGHT, DROPDOWN_PADDING_Y } from 'HubStyleTokens/sizes';
import getLang from 'I18n/utils/getLang';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIInlineLabel from 'UIComponents/form/UIInlineLabel';
import UISelect from 'UIComponents/input/UISelect';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UICheckbox from 'UIComponents/input/UICheckbox';
import * as PlanType from 'ecosystem-marketplace-core/constants/pricings/PlanType';
import * as FilterKey from '../../../constants/search/FilterKey';
import * as SortField from '../../../constants/search/SortField';
import * as SortOrder from '../../../constants/search/SortOrder';
import { getIsUngatedForMultiCurrency } from '../../../selectors/auth';
const MENU_ITEM_HEIGHT = parseInt(DROPDOWN_ITEM_DEFAULT_HEIGHT, 10);
const MENU_PADDING_Y = parseInt(DROPDOWN_PADDING_Y, 10);
const fieldOrderTuples = Immutable.OrderedSet([[SortField.RELEVANCE, SortOrder.DESC], [SortField.HUB_INSTALLS, SortOrder.DESC], [SortField.FIRST_PUBLISHED, SortOrder.DESC], [SortField.PRICE, SortOrder.ASC], [SortField.PRICE, SortOrder.DESC], [SortField.NAME, SortOrder.ASC], [SortField.NAME, SortOrder.DESC]]);

const getI18nKey = key => `appMarketplace.storefront.results.sort.${key}`;

const getOptionI18nKey = (sortField, sortOrder) => {
  const key = (() => {
    switch (sortField) {
      case SortField.RELEVANCE:
        return 'relevance';

      case SortField.HUB_INSTALLS:
        return 'mostInstalled';

      case SortField.FIRST_PUBLISHED:
        return 'newest';

      case SortField.PRICE:
        return sortOrder === SortOrder.ASC ? 'priceLowHigh' : 'priceHighLow';

      case SortField.NAME:
        return sortOrder === SortOrder.ASC ? 'name' : 'nameReverse';

      default:
        return null;
    }
  })();

  return getI18nKey(`options.${key}`);
};

const getOptionValue = (field, order) => `${field}:${order}`;

const SortSelectItem = ({
  children,
  option
}) => {
  return /*#__PURE__*/_jsx(UITooltip, {
    delay: 200,
    disabled: !option.tooltip,
    placement: "left",
    title: option.tooltip,
    children: children
  });
};

const SortDropdown = ({
  disabled,
  filters,
  onSortChange,
  onFilterChange
}) => {
  const isUngatedForMultiCurrency = useSelector(getIsUngatedForMultiCurrency);
  const {
    [FilterKey.PRICING]: planType,
    [FilterKey.SEARCH]: searchValue,
    [FilterKey.SORT_FIELD]: sortField,
    [FilterKey.SORT_ORDER]: sortOrder,
    [FilterKey.SHOW_PREFERRED_LANG_FIRST]: showPreferredLangFirst
  } = filters;
  const handleChange = useCallback(({
    target: {
      value
    }
  }) => {
    const [field, order] = value.split(':');
    onSortChange(field, order);
  }, [onSortChange]);
  const [defaultField, defaultOrder] = useMemo(() => {
    return searchValue ? [SortField.RELEVANCE, SortOrder.DESC] : [SortField.HUB_INSTALLS, SortOrder.DESC];
  }, [searchValue]);
  const value = useMemo(() => {
    let field = defaultField;
    let order = defaultOrder;

    if (sortField) {
      field = sortField;
      order = sortOrder || defaultOrder;
    }

    return getOptionValue(field, order);
  }, [defaultField, defaultOrder, sortField, sortOrder]);
  const options = useMemo(() => {
    return fieldOrderTuples.filter(([field]) => {
      if (isUngatedForMultiCurrency && field === SortField.PRICE) {
        return false;
      }

      if (field === SortField.RELEVANCE && !searchValue) {
        return false;
      }

      return true;
    }).map(([field, order]) => {
      const option = {
        value: getOptionValue(field, order),
        text: unescapedText(getOptionI18nKey(field, order))
      };

      if (field === SortField.PRICE) {
        option.disabled = planType === null || planType === PlanType.FREE;
        option.tooltip = option.disabled ? unescapedText(getI18nKey('priceTooltip')) : null;
      }

      return option;
    }).toArray();
  }, [isUngatedForMultiCurrency, planType, searchValue]);
  const totalOptions = options.length;
  const minHeight = useMemo(() => totalOptions * MENU_ITEM_HEIGHT + MENU_PADDING_Y * 2, [totalOptions]);
  const language = getLang();
  const showPreferredLangToggle = useMemo(() => /*#__PURE__*/_jsx(UICheckbox, {
    checked: showPreferredLangFirst,
    className: "m-top-1",
    onChange: ({
      target: {
        checked
      }
    }) => {
      onFilterChange(FilterKey.SHOW_PREFERRED_LANG_FIRST, checked);
    },
    size: "small",
    style: {
      maxWidth: 200
    },
    children: /*#__PURE__*/_jsx("span", {
      style: {
        lineHeight: '24px',
        position: 'relative',
        top: -3
      },
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: getI18nKey('showPreferredLangFirst'),
        options: {
          language: unescapedText(`SharedI18nStrings.languageNames.${language}`)
        }
      })
    })
  }), [language, onFilterChange, showPreferredLangFirst]);
  return /*#__PURE__*/_jsx(UIInlineLabel, {
    className: "m-right-0",
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: getI18nKey('label')
    }),
    children: /*#__PURE__*/_jsx(UISelect, {
      "data-selenium-id": "storefront-sort-dropdown",
      buttonUse: "transparent",
      className: "p-right-0",
      disabled: disabled,
      dropdownFooter: showPreferredLangToggle,
      itemComponent: SortSelectItem,
      menuStyle: {
        minHeight
      },
      menuWidth: "auto",
      onChange: handleChange,
      options: options,
      value: value
    })
  });
};

SortDropdown.propTypes = {
  disabled: PropTypes.bool,
  filters: CustomPropTypes.storefrontFilters.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired
};
export default SortDropdown;